import type { SourceType, Resolution } from '../types/obs';

// Source metadata matching the original implementation
export interface SourceMeta {
  label: string;
  vid: string;
  mix: number;
  dot: string;
  icon: string;
  settings: Record<string, unknown>;
  hk: Record<string, unknown[]>;
}

export const SMETA: Record<string, SourceMeta> = {
  dshow_input: {
    label: 'Video Capture',
    vid: 'dshow_input',
    mix: 255,
    dot: 'cam',
    icon: 'cam',
    settings: { video_device_id: '', last_video_device_id: '' },
    hk: { 'libobs.mute': [], 'libobs.unmute': [], 'libobs.push-to-mute': [], 'libobs.push-to-talk': [] }
  },
  av_capture_input: {
    label: 'Video Capture',
    vid: 'av_capture_input',
    mix: 255,
    dot: 'cam',
    icon: 'cam',
    settings: {},
    hk: { 'libobs.mute': [], 'libobs.unmute': [], 'libobs.push-to-mute': [], 'libobs.push-to-talk': [] }
  },
  monitor_capture: {
    label: 'Display',
    vid: 'monitor_capture',
    mix: 0,
    dot: 'screen',
    icon: 'screen',
    settings: {},
    hk: {}
  },
  window_capture: {
    label: 'Window',
    vid: 'window_capture',
    mix: 0,
    dot: 'screen',
    icon: 'screen',
    settings: {},
    hk: {}
  },
  game_capture: {
    label: 'Game',
    vid: 'game_capture',
    mix: 255,
    dot: 'screen',
    icon: 'screen',
    settings: {},
    hk: { 'libobs.mute': [], 'libobs.unmute': [], 'libobs.push-to-mute': [], 'libobs.push-to-talk': [], 'hotkey_start': [], 'hotkey_stop': [] }
  },
  browser_source: {
    label: 'Browser',
    vid: 'browser_source',
    mix: 255,
    dot: 'browser',
    icon: 'browser',
    settings: { width: 1920, height: 200, fps: 30, css: '' },
    hk: { 'libobs.mute': [], 'libobs.unmute': [], 'libobs.push-to-mute': [], 'libobs.push-to-talk': [], 'ObsBrowser.Refresh': [] }
  },
  image_source: {
    label: 'Image',
    vid: 'image_source',
    mix: 0,
    dot: 'image',
    icon: 'image',
    settings: {},
    hk: {}
  },
  color_source: {
    label: 'Color',
    vid: 'color_source_v3',
    mix: 0,
    dot: 'color',
    icon: 'color',
    settings: {},
    hk: {}
  },
  ffmpeg_source: {
    label: 'Media',
    vid: 'ffmpeg_source',
    mix: 255,
    dot: 'media',
    icon: 'media',
    settings: {},
    hk: { 'libobs.mute': [], 'libobs.unmute': [], 'libobs.push-to-mute': [], 'libobs.push-to-talk': [], 'MediaSource.Restart': [], 'MediaSource.Play': [], 'MediaSource.Pause': [], 'MediaSource.Stop': [] }
  },
  text_gdiplus: {
    label: 'GDI+ Text',
    vid: 'text_gdiplus_v3',
    mix: 0,
    dot: 'text',
    icon: 'text',
    settings: {},
    hk: {}
  },
  text_ft2_source: {
    label: 'FreeType',
    vid: 'text_ft2_source',
    mix: 0,
    dot: 'text',
    icon: 'text',
    settings: {},
    hk: {}
  },
  wasapi_input_capture: {
    label: 'Audio In',
    vid: 'wasapi_input_capture',
    mix: 255,
    dot: 'other',
    icon: 'screen',
    settings: {},
    hk: { 'libobs.mute': [], 'libobs.unmute': [], 'libobs.push-to-mute': [], 'libobs.push-to-talk': [] }
  },
};

// Source block CSS classes
export const SBCLS: Record<string, string> = {
  dshow_input: 'sb-cam',
  av_capture_input: 'sb-cam',
  monitor_capture: 'sb-screen',
  window_capture: 'sb-screen',
  game_capture: 'sb-screen',
  browser_source: 'sb-browser',
  image_source: 'sb-image',
  color_source: 'sb-color',
  ffmpeg_source: 'sb-media',
  text_gdiplus: 'sb-text',
  text_ft2_source: 'sb-text',
};

// Source icon CSS classes
export const SICLS: Record<string, string> = {
  cam: 'si-cam',
  screen: 'si-screen',
  browser: 'si-browser',
  image: 'si-image',
  media: 'si-media',
  text: 'si-text',
  color: 'si-color',
  other: 'si-screen',
};

// Dot colors
export const DCOL: Record<string, string> = {
  cam: '#22c55e',
  screen: '#3b82f6',
  browser: '#8b5cf6',
  image: '#ec4899',
  color: '#6b7280',
  media: '#f59e0b',
  text: '#fbbf24',
  other: '#475569',
};

// Video sources that need cropping
export const VSRC: Record<string, boolean> = {
  dshow_input: true,
  av_capture_input: true,
  monitor_capture: true,
  window_capture: true,
  game_capture: true,
  ffmpeg_source: true,
};

export function getDot(type: SourceType | string): string {
  return (SMETA[type]?.dot) || 'other';
}

export function getSB(type: SourceType | string): string {
  return SBCLS[type] || 'sb-other';
}

export function getSI(type: SourceType | string): string {
  return SICLS[getDot(type)] || 'si-screen';
}

export function getIcon(type: SourceType | string): string {
  return (SMETA[type]?.icon) || 'screen';
}

export function getLabel(type: SourceType | string): string {
  return (SMETA[type]?.label) || type;
}

// Generate UUID
export function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// Calculate crop for video sources
export interface CropResult {
  l: number;
  t: number;
  r: number;
  b: number;
}

export function calcCrop(
  type: SourceType | string,
  bw: number,
  bh: number,
  cw: number,
  ch: number
): CropResult {
  if (!VSRC[type]) {
    return { l: 0, t: 0, r: 0, b: 0 };
  }

  const sc = bh / ch;
  const sw = cw * sc;
  const ew = sw - bw;

  if (ew > 0) {
    const c = Math.round(ew / 2 / sc);
    return { l: c, t: 0, r: c, b: 0 };
  }

  const sh = ch * (bw / cw);
  const eh = sh - bh;

  if (eh > 0) {
    const ct = Math.round(eh / 2 / (bw / cw));
    return { l: 0, t: ct, r: 0, b: ct };
  }

  return { l: 0, t: 0, r: 0, b: 0 };
}

// Resolution presets
export const RESOLUTIONS: Resolution[] = [
  { id: '1080p', name: '1080p', w: 1920, h: 1080, label: '1920×1080' },
  { id: '720p', name: '720p', w: 1280, h: 720, label: '1280×720' },
  { id: '4k', name: '4K', w: 3840, h: 2160, label: '3840×2160' },
  { id: 'vertical', name: 'Vertical', w: 1080, h: 1920, label: '1080×1920 (9:16)' },
];
